/**
 * defineDetailSurface - one page's binding of the shared detail-page surface.
 *
 * Matter and review grew the same shell twice and it drifted: review kept a
 * copy of the drag gesture without the window-level net (a drag that could be
 * started and never finished), and a canvas inset on two sides instead of
 * four (panes and canvas on different baselines). Both are the same page
 * pattern - a canvas with glass panes floating over its edges - so a page now
 * DECLARES its panes and gets the shell, the rail, the persistence and the
 * context for free.
 *
 *     const MATTER_SURFACE = defineDetailSurface<MatterPanelId, MatterTab>({
 *       kind: 'matter', name: 'workspace', scope: 'entity',
 *       ids: ['context', 'reader'],
 *       panes: {
 *         context: { label: 'Context', bounds: {min:240,max:720},
 *                    dock: 'left', index: 0, width: 300, visible: true, rail: true },
 *         reader:  { label: 'Reader',  bounds: {min:320,max:900},
 *                    dock: 'right', index: 1, width: 560 },
 *       },
 *       tabs: ['Details', 'Documents'],
 *     });
 *
 * DECLARE ONLY THE PANES THE PAGE PRODUCES. A pane in the config that nothing
 * ever fills is a pane that can open onto nothing, and the project pilot had
 * to ship an empty reader component to keep one honest. If nothing calls
 * `openReader`, leave the reader pane (and `readerPane`) out entirely.
 *
 * See `README.md` in this directory for the whole recipe.
 *
 * TWO THINGS ARE NOT OBVIOUS AND MATTER:
 *
 *   THE KEYED CACHE. `usePanelSurface` memoises every callback on surface
 *   IDENTITY, so handing it a freshly built object each render would rebuild
 *   them all and defeat the point. `surfaceFor` caches per user + entity.
 *
 *   THE SCOPE. `scope: 'entity'` puts the record's id in the storage key, so
 *   each matter remembers its own arrangement; `scope: 'global'` gives the
 *   page one arrangement, which is what a review session wants.
 */

import type { ReactNode } from 'react';
import {
  DockSide,
  PanelBounds,
  PanelLayout,
  PanelSlot,
  PanelSurface,
} from './panelLayout';
import { LegacyLayoutRule, scopeKey } from '../../../services/layout/layoutStore';
import { PaneRailItem } from './PaneRail';

/** One pane: what it is called, how far it resizes, where it starts. */
export interface DetailPaneConfig {
  /** Shown on the rail pill and used as the pane's default title. */
  label: string;
  bounds: PanelBounds;
  dock: DockSide;
  /** Position within its dock, top to bottom. */
  index: number;
  width: number;
  /** Open on a first visit. Defaults to closed. */
  visible?: boolean;
  /**
   * Give this pane a toggle on the rail.
   *
   * Off for panes that open because something happened rather than because
   * someone asked - a detail container opens on a selection, a reader opens
   * on a document - since a toggle for those reads as a control that does
   * nothing until you use the page.
   */
  rail?: boolean;
}

export interface DetailSurfaceConfig<Id extends string, Tab extends string = string> {
  /** Family of surface, and the `kind` segment of the storage key. */
  kind: string;
  /**
   * The surface within that family.
   *
   * Doubles as the `data-testid` PREFIX for the shell - `<name>-surface`,
   * `<name>-canvas`, `<name>-dock-left`, `<name>-sheet`.
   */
  name: string;
  /** Bumped when the stored layout model changes shape. Defaults to `v1`. */
  version?: string;
  /** `entity` remembers one arrangement per record; `global`, one per page. */
  scope: 'entity' | 'global';
  ids: Id[];
  panes: Record<Id, DetailPaneConfig>;
  /**
   * The canvas's tabs, in bar order - the VALUES, not the labels.
   *
   * The page still owns the human label (it needs `t()`, an icon, a tooltip,
   * a licence check); what belongs here is the vocabulary, so the hook can
   * tell a real tab from a stale `?tab=` in someone else's link and so
   * `goToTab` cannot be handed a tab that does not exist. Leave it off and
   * every string is accepted, which is what a surface with no tab bar wants.
   */
  tabs?: Tab[];
  /** Raised and cleared with the selection, if this page has one. */
  detailPane?: Id;
  /** Where `openReader` puts its content, if this page has a reader. */
  readerPane?: Id;
  /**
   * Unscoped keys this surface used to write, migrated once into the user's
   * namespace so nobody's panes move on the day the scoping ships.
   *
   * `exact` is one key; `prefix` matches a key per record, and everything
   * after it is the entity id.
   */
  legacyKeys?: Array<{ exact?: string; prefix?: string }>;
}

export interface DetailSurfaceDefinition<Id extends string, Tab extends string = string> {
  config: DetailSurfaceConfig<Id, Tab>;
  ids: Id[];
  /** The declared tabs, in bar order. Empty when the surface declares none. */
  tabs: Tab[];
  /** The tab a page opens on when the URL asks for nothing, or for nonsense. */
  defaultTab: Tab | '';
  /** True for a declared tab - and for anything, if none were declared. */
  isTab: (value: unknown) => value is Tab;
  /**
   * The tab to actually show for a raw `?tab=` value.
   *
   * An unknown tab is IGNORED rather than honoured: a stale link, a renamed
   * tab or someone else's URL should land on the page's first tab, not on a
   * blank canvas.
   */
  resolveTab: (raw: string | null | undefined) => Tab | '';
  bounds: Record<Id, PanelBounds>;
  labels: Record<Id, string>;
  defaults: PanelLayout<Id>;
  /** The unscoped keys to migrate, ready for `migrateLegacyLayouts`. */
  legacyRules: LegacyLayoutRule[];
  /** The `PanelSurface` for one user and (when entity-scoped) one record. */
  surfaceFor: (userId: string, entityId?: string) => PanelSurface<Id>;
  /**
   * The surface keyed the OLD, unscoped way.
   *
   * Kept for the pure-function bindings and their tests, and as the source of
   * the migration. Nothing at runtime reads or writes through it.
   */
  legacySurface: (entityId?: string) => PanelSurface<Id>;
  /** Rail pills for every pane that asked for one, in declaration order. */
  railItems: (options?: RailItemOptions<Id>) => PaneRailItem<Id>[];
}

export interface RailItemOptions<Id extends string> {
  /** Usually i18next's `t`. Identity when omitted. */
  t?: (key: string) => string;
  icons?: Partial<Record<Id, ReactNode>>;
  /** Small tally on a pill - zero renders nothing. */
  badges?: Partial<Record<Id, number>>;
  /** Drop pills the page has nothing to show in right now. */
  omit?: Id[];
}

const identity = (key: string) => key;

export function defineDetailSurface<Id extends string, Tab extends string = string>(
  config: DetailSurfaceConfig<Id, Tab>
): DetailSurfaceDefinition<Id, Tab> {
  const { ids, panes, kind, name, version = 'v1', scope } = config;

  const tabs = config.tabs ?? [];
  const defaultTab: Tab | '' = tabs[0] ?? '';
  /* No declared tabs means the surface has no tab vocabulary to check against
     - a review session's canvas is a finding, not a tab bar - so everything
     passes and nothing is rewritten. */
  const isTab = (value: unknown): value is Tab =>
    tabs.length === 0
      ? typeof value === 'string'
      : tabs.includes(value as Tab);
  const resolveTab = (raw: string | null | undefined): Tab | '' =>
    raw && isTab(raw) ? raw : defaultTab;

  const bounds = {} as Record<Id, PanelBounds>;
  const labels = {} as Record<Id, string>;
  const slots = {} as Record<Id, PanelSlot<Id>>;
  ids.forEach((id) => {
    const pane = panes[id];
    bounds[id] = pane.bounds;
    labels[id] = pane.label;
    slots[id] = {
      id,
      dock: pane.dock,
      index: pane.index,
      width: pane.width,
      visible: pane.visible ?? false,
    };
  });

  const defaults: PanelLayout<Id> = { slots, front: null };

  const legacyRules: LegacyLayoutRule[] = (config.legacyKeys ?? []).map((entry) => ({
    ...entry,
    scope: { kind, name, version },
  }));

  /*
    Cached by user + entity, because `usePanelSurface` keys its callbacks on
    surface identity. A fresh object per render would rebuild all of them.
  */
  const cache = new Map<string, PanelSurface<Id>>();

  const surfaceFor = (userId: string, entityId?: string): PanelSurface<Id> => {
    const entity = scope === 'entity' ? entityId || 'unknown' : undefined;
    const cacheKey = `${userId} ${entity ?? ''}`;
    const existing = cache.get(cacheKey);
    if (existing) return existing;

    const surface: PanelSurface<Id> = {
      ids,
      bounds,
      defaults,
      storageKey: scopeKey({ kind, name, version, userId, entityId: entity }),
    };
    cache.set(cacheKey, surface);
    return surface;
  };

  const legacyCache = new Map<string, PanelSurface<Id>>();

  const legacySurface = (entityId?: string): PanelSurface<Id> => {
    const entity = scope === 'entity' ? entityId || 'unknown' : '';
    const existing = legacyCache.get(entity);
    if (existing) return existing;

    const rule = (config.legacyKeys ?? [])[0];
    const storageKey = rule?.prefix
      ? `${rule.prefix}${entity}`
      : rule?.exact ?? scopeKey({ kind, name, version, userId: 'legacy', entityId: entity });

    const surface: PanelSurface<Id> = { ids, bounds, defaults, storageKey };
    legacyCache.set(entity, surface);
    return surface;
  };

  const railItems = ({
    t = identity,
    icons,
    badges,
    omit,
  }: RailItemOptions<Id> = {}): PaneRailItem<Id>[] =>
    ids
      .filter((id) => panes[id].rail && !omit?.includes(id))
      .map((id) => ({
        id,
        label: t(panes[id].label),
        icon: icons?.[id],
        badge: badges?.[id],
      }));

  return {
    config,
    ids,
    tabs,
    defaultTab,
    isTab,
    resolveTab,
    bounds,
    labels,
    defaults,
    legacyRules,
    surfaceFor,
    legacySurface,
    railItems,
  };
}

export default defineDetailSurface;
